import React, { useState, useContext } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import {
  Type, List, ImageIcon, Settings, Trash2, ArrowRight, Layout,
  Layers, Edit2, Plus, Lock, Unlock,
  AlignLeft, AlignCenter, AlignRight,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical,
  ArrowUpDown, ArrowLeftRight, CheckCircle, MousePointer,
  Palette, ChevronDown, ChevronUp, Code
} from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import NumericInput from './NumericInput';
// import { getDefaultShape } from '../../Survey/BodyMap/geometry'; // Disabled with per-region edit UI
import { RESPONSE_OPTION_PRESETS, QUESTION_TYPE_DISPLAY } from './constants';

// Button style presets
const BUTTON_STYLE_PRESETS = {
  primary: {
    name: 'Primary',
    backgroundColor: '#0d9488',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#0d9488'
  },
  secondary: {
    name: 'Secondary',
    backgroundColor: '#6b7280',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#6b7280'
  },
  success: {
    name: 'Success',
    backgroundColor: '#22c55e',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#22c55e'
  },
  danger: {
    name: 'Danger',
    backgroundColor: '#ef4444',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#ef4444'
  },
  outline: {
    name: 'Outline',
    backgroundColor: 'transparent',
    textColor: '#0d9488',
    borderRadius: '8',
    borderWidth: '2',
    borderColor: '#0d9488'
  },
  pill: {
    name: 'Pill',
    backgroundColor: '#0d9488',
    textColor: '#ffffff',
    borderRadius: '9999',
    borderWidth: '0',
    borderColor: '#0d9488'
  }
};

// Platform/Theme button styles
const BUTTON_THEME_PRESETS = {
  default: {
    name: 'Default',
    description: 'Standard web button',
    backgroundColor: '#0d9488',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#0d9488',
    fontWeight: '600'
  },
  ios: {
    name: 'iOS',
    description: 'Apple iOS style',
    backgroundColor: '#007AFF',
    textColor: '#ffffff',
    borderRadius: '12',
    borderWidth: '0',
    borderColor: '#007AFF',
    fontWeight: '600'
  },
  android: {
    name: 'Android / Material',
    description: 'Google Material Design',
    backgroundColor: '#1976D2',
    textColor: '#ffffff',
    borderRadius: '4',
    borderWidth: '0',
    borderColor: '#1976D2',
    fontWeight: '500'
  },
  windows: {
    name: 'Windows / Fluent',
    description: 'Microsoft Fluent Design',
    backgroundColor: '#0078D4',
    textColor: '#ffffff',
    borderRadius: '4',
    borderWidth: '0',
    borderColor: '#0078D4',
    fontWeight: '600'
  },
  macos: {
    name: 'macOS',
    description: 'Apple macOS style',
    backgroundColor: '#007AFF',
    textColor: '#ffffff',
    borderRadius: '6',
    borderWidth: '0',
    borderColor: '#007AFF',
    fontWeight: '500'
  },
  bootstrap: {
    name: 'Bootstrap',
    description: 'Bootstrap framework style',
    backgroundColor: '#0d6efd',
    textColor: '#ffffff',
    borderRadius: '6',
    borderWidth: '0',
    borderColor: '#0d6efd',
    fontWeight: '400'
  },
  flat: {
    name: 'Flat / Minimal',
    description: 'Clean flat design',
    backgroundColor: '#3498db',
    textColor: '#ffffff',
    borderRadius: '0',
    borderWidth: '0',
    borderColor: '#3498db',
    fontWeight: '600'
  },
  rounded: {
    name: 'Rounded / Soft',
    description: 'Soft rounded corners',
    backgroundColor: '#6366f1',
    textColor: '#ffffff',
    borderRadius: '24',
    borderWidth: '0',
    borderColor: '#6366f1',
    fontWeight: '500'
  },
  glassmorphism: {
    name: 'Glass / Modern',
    description: 'Glassmorphism effect',
    backgroundColor: 'rgba(255,255,255,0.2)',
    textColor: '#1f2937',
    borderRadius: '12',
    borderWidth: '1',
    borderColor: 'rgba(255,255,255,0.3)',
    fontWeight: '500'
  },
  neumorphism: {
    name: 'Neumorphic',
    description: 'Soft UI shadows',
    backgroundColor: '#e0e5ec',
    textColor: '#6b7280',
    borderRadius: '12',
    borderWidth: '0',
    borderColor: '#e0e5ec',
    fontWeight: '600'
  }
};

const FONT_FAMILY_OPTIONS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'system-ui, sans-serif', label: 'System UI' }
];

const FONT_WEIGHT_OPTIONS = [
  { value: '400', label: 'Normal' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi-Bold' },
  { value: '700', label: 'Bold' }
];

// Which numeric weights each system font actually ships. System fonts
// (Arial, Times, Georgia, etc.) only have 400/700 — selecting 500 or 600
// silently falls back to 400, which is why Medium/Semi-Bold look identical
// to Normal on those fonts. system-ui resolves to Segoe UI Variable / SF Pro
// on modern OSes which support the full range.
const FONT_WEIGHT_SUPPORT = {
  'Arial, sans-serif': ['400', '700'],
  '"Times New Roman", serif': ['400', '700'],
  'Helvetica, Arial, sans-serif': ['400', '700'],
  'Georgia, serif': ['400', '700'],
  'Verdana, sans-serif': ['400', '700'],
  '"Courier New", monospace': ['400', '700'],
  'system-ui, sans-serif': ['400', '500', '600', '700'],
};

const TEXT_ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left', icon: AlignLeft },
  { value: 'center', label: 'Center', icon: AlignCenter },
  { value: 'right', label: 'Right', icon: AlignRight }
];

const PropertiesPanel = ({
  selectedElementData,
  selectedElement,
  screens,
  currentScreenIndex,
  setScreens,
  setConfirmDialog,
  calculateQuestionHeight,
  updateElementContent,
  distributeOptions,
  alignOptions,
  toggleLockElement,
  isElementLocked,
  deleteElement,
  ungroupQuestion,
  regroupQuestion,
  updateSubElementPosition
}) => {
  const { user } = useContext(AuthContext);
  const [showAdvancedCss, setShowAdvancedCss] = useState(false);

  const updateElementStyle = (property, value) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx !== currentScreenIndex) return screen;
      return {
        ...screen,
        elements: screen.elements.map(el =>
          el.id === selectedElement
            ? {
                ...el,
                style: { ...(el.style || {}), [property]: value }
              }
            : el
        )
      };
    });
    setScreens(updatedScreens);
  };

  // Helper to update button style properties
  const updateButtonStyle = (property, value) => {
    updateElementStyle(property, value);
  };

  // Apply a style preset to the button
  const applyStylePreset = (presetKey) => {
    const preset = BUTTON_STYLE_PRESETS[presetKey];
    if (!preset) return;
    
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { 
                  ...el, 
                  style: { 
                    ...(el.style || {}),
                    backgroundColor: preset.backgroundColor,
                    textColor: preset.textColor,
                    borderRadius: preset.borderRadius,
                    borderWidth: preset.borderWidth,
                    borderColor: preset.borderColor,
                    preset: presetKey
                  }
                }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  // Apply a theme preset to the button
  const applyThemePreset = (themeKey) => {
    const theme = BUTTON_THEME_PRESETS[themeKey];
    if (!theme) return;
    
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { 
                  ...el, 
                  style: { 
                    ...(el.style || {}),
                    backgroundColor: theme.backgroundColor,
                    textColor: theme.textColor,
                    borderRadius: theme.borderRadius,
                    borderWidth: theme.borderWidth,
                    borderColor: theme.borderColor,
                    fontWeight: theme.fontWeight,
                    theme: themeKey
                  }
                }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  if (!selectedElementData) {
    return (
      <div className="text-center mt-12 p-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center">
          <Settings className="w-8 h-8 text-gray-400" />
        </div>
        <h4 className="text-sm font-semibold text-gray-700 mb-1">No Element Selected</h4>
        <p className="text-xs text-gray-500">Click on an element in the canvas to edit its properties</p>
      </div>
    );
  }

  const handleAddOption = () => {
    const currentOptions = selectedElementData.responseOptions || [];
    const newOption = { 
      order: currentOptions.length + 1, 
      label: `Option ${currentOptions.length + 1}`, 
      value: currentOptions.length 
    };
    const updatedOptions = [...currentOptions, newOption];
    
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el => {
            if (el.id === selectedElement) {
              const newHeight = calculateQuestionHeight(
                { ...el.data, options: updatedOptions },
                el.responseConfig || {}
              );
              
              return { 
                ...el, 
                responseOptions: updatedOptions,
                data: {
                  ...el.data,
                  options: updatedOptions
                },
                size: {
                  ...el.size,
                  height: newHeight
                }
              };
            }
            return el;
          })
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handlePresetChange = (e) => {
    if (!e.target.value) return;
    
    const presetValue = e.target.value;
    const existingOptions = selectedElementData.responseOptions || [];
    
    const applyPreset = () => {
      const updatedScreens = screens.map((screen, idx) => {
        if (idx === currentScreenIndex) {
          return {
            ...screen,
            elements: screen.elements.map(el => {
              if (el.id === selectedElement) {
                const newHeight = calculateQuestionHeight(
                  { ...el.data, options: RESPONSE_OPTION_PRESETS[presetValue] },
                  el.responseConfig || {}
                );
                
                return { 
                  ...el, 
                  responseOptions: RESPONSE_OPTION_PRESETS[presetValue],
                  data: {
                    ...el.data,
                    options: RESPONSE_OPTION_PRESETS[presetValue]
                  },
                  size: {
                    ...el.size,
                    height: newHeight
                  }
                };
              }
              return el;
            })
          };
        }
        return screen;
      });
      setScreens(updatedScreens);
      e.target.value = '';
    };

    if (existingOptions.length > 0) {
      setConfirmDialog({
        isOpen: true,
        title: 'Replace Response Options?',
        message: `This will replace ${existingOptions.length} existing response option(s).\n\nAre you sure you want to continue?`,
        type: 'warning',
        onConfirm: applyPreset
      });
      e.target.value = '';
    } else {
      applyPreset();
    }
  };

  const handleOptionLabelChange = (index, html) => {
    const updatedOptions = [...(selectedElementData.responseOptions || [])];
    updatedOptions[index].label = html;
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { 
                  ...el, 
                  responseOptions: updatedOptions,
                  data: {
                    ...el.data,
                    options: updatedOptions
                  }
                }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleOptionValueChange = (index, value) => {
    const updatedOptions = [...(selectedElementData.responseOptions || [])];
    updatedOptions[index].value = Number(value);
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { 
                  ...el, 
                  responseOptions: updatedOptions,
                  data: {
                    ...el.data,
                    options: updatedOptions
                  }
                }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleDeleteOption = (index) => {
    const updatedOptions = (selectedElementData.responseOptions || []).filter((_, i) => i !== index);
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el => {
            if (el.id === selectedElement) {
              const newHeight = calculateQuestionHeight(
                { ...el.data, options: updatedOptions },
                el.responseConfig || {}
              );
              
              return { 
                ...el, 
                responseOptions: updatedOptions,
                data: {
                  ...el.data,
                  options: updatedOptions
                },
                size: {
                  ...el.size,
                  height: newHeight
                }
              };
            }
            return el;
          })
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleRequiredChange = (checked) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, required: checked }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleMultiSelectChange = (checked) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, multiSelect: checked }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleResponseConfigChange = (key, value) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, responseConfig: { ...el.responseConfig, [key]: value } }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  // Compute where a group of given width should sit inside containerWidth
  // under a given alignment (left / center / right).
  const alignmentOffset = (align, containerWidth, groupWidth) => {
    if (align === 'center') return (containerWidth - groupWidth) / 2;
    if (align === 'right') return containerWidth - groupWidth;
    return 0;
  };

  const SCALE_SUB_TYPES = [
    'question-vas', 'question-nrs', 'question-vrs', 'question-segmented',
    'question-emoji', 'question-thermometer', 'question-stepped-slider', 'question-text-input'
  ];

  // Update responseConfig.optionsAlignment. If the element is ungrouped,
  // shift every option/scale sub by the delta between the old and new
  // aligned positions so Custom Layout visually matches grouped mode.
  const updateOptionsAlignment = (newAlignment) => {
    const el = selectedElementData;
    if (!el) return;
    const oldAlignment = el.responseConfig?.optionsAlignment ?? 'left';
    if (oldAlignment === newAlignment) return;

    const updatedScreens = screens.map((screen, idx) => {
      if (idx !== currentScreenIndex) return screen;
      return {
        ...screen,
        elements: screen.elements.map(e => {
          if (e.id !== selectedElement) return e;
          const nextConfig = { ...e.responseConfig, optionsAlignment: newAlignment };
          if (!e.isUngrouped || !Array.isArray(e.subElements)) {
            return { ...e, responseConfig: nextConfig };
          }
          // Identify the horizontal extent of the group being aligned.
          const groupSubs = e.subElements.filter(
            s => s.type === 'question-option' || SCALE_SUB_TYPES.includes(s.type)
          );
          if (groupSubs.length === 0) {
            return { ...e, responseConfig: nextConfig };
          }
          const minX = Math.min(...groupSubs.map(s => s.position.x));
          const maxX = Math.max(...groupSubs.map(s => s.position.x + s.size.width));
          const groupWidth = maxX - minX;
          const containerWidth = e.size?.width || 0;
          const oldOffset = alignmentOffset(oldAlignment, containerWidth, groupWidth);
          const newOffset = alignmentOffset(newAlignment, containerWidth, groupWidth);
          const delta = newOffset - oldOffset;
          if (delta === 0) {
            return { ...e, responseConfig: nextConfig };
          }
          const updatedSubs = e.subElements.map(sub => {
            if (sub.type === 'question-option' || SCALE_SUB_TYPES.includes(sub.type)) {
              return { ...sub, position: { ...sub.position, x: sub.position.x + delta } };
            }
            return sub;
          });
          return { ...e, responseConfig: nextConfig, subElements: updatedSubs };
        })
      };
    });
    setScreens(updatedScreens);
  };

  const handleElementCodeChange = (code) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, code: code }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleInputPropertyChange = (property, value) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, [property]: value }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  // Check if element is an input type that should have a code
  const isInputElement = ['text-input', 'number-input', 'date-picker', 'checkbox'].includes(selectedElementData?.type);
  const showTypographyControls = ['question', 'text', 'instruction'].includes(selectedElementData?.type);
  const responseLayout = selectedElementData.responseConfig?.layout || '';
  const showResponseFormattingControls = selectedElementData.type === 'question';
  const usesButtonOptions = ['outline', 'filled', 'solid'].includes(selectedElementData.responseConfig?.buttonStyle) ||
    (selectedElementData.responseConfig?.display || '').includes('button');
  const showRadioSizeControl = !['vas', 'nrs', 'vrs-unipolar', 'vrs-bipolar', 'text', 'dropdown'].includes(responseLayout) && !usesButtonOptions;

  return (
    <div className="space-y-5">
      {/* Element Info Card */}
      <div className="bg-gradient-to-br from-white dark:from-gray-800 to-gray-50 dark:to-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-primary-100 text-primary-600 rounded-xl">
            {selectedElementData.type === 'question' ? <List className="w-5 h-5" /> :
             selectedElementData.type === 'instruction' ? <Type className="w-5 h-5" /> :
             selectedElementData.type === 'text' ? <Type className="w-5 h-5" /> :
             selectedElementData.type === 'button' ? <ArrowRight className="w-5 h-5" /> :
             selectedElementData.type === 'image' ? <ImageIcon className="w-5 h-5" /> :
             <Layers className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              Element Type
            </label>
            <p className="text-base text-gray-900 capitalize font-semibold">
              {selectedElementData.type.replace('-', ' ')}
              {selectedElementData.type === 'question' && (() => {
                const qType = selectedElementData.data?.type || selectedElementData.data?.response_type;
                const displayName = QUESTION_TYPE_DISPLAY[qType] || qType;
                return displayName ? <span className="text-primary-600"> — {displayName}</span> : null;
              })()}
            </p>
          </div>
          {selectedElementData.type === 'question' && selectedElementData.isLibraryItem && (
            <div className="flex items-center gap-1">
              <span className="px-2 py-1 text-[9px] font-semibold bg-amber-100 text-amber-700 rounded-md border border-amber-200">
                🔒 Library Item
              </span>
              {selectedElementData.isModified && (
                <span
                  title="This item has been edited since import"
                  className="inline-flex items-center gap-1 px-2 py-1 text-[9px] font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700"
                >
                  <Edit2 className="w-2.5 h-2.5" />
                  Modified
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Question Settings - Required, Multi-select */}
      {selectedElementData.type === 'question' && (() => {
        const qLayout = selectedElementData.responseConfig?.layout;
        const qType = selectedElementData.data?.type || selectedElementData.itemData?.question_type;
        const hideMultiSelect = ['vas', 'nrs', 'text', 'boolean', 'vrs-unipolar', 'vrs-bipolar'].includes(qLayout) ||
          ['vas', 'numeric', 'text', 'boolean', 'ordinal', 'likert', 'body_map'].includes(qType);
        return (
          <div className="bg-gradient-to-br from-white dark:from-gray-800 to-gray-50 dark:to-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
                <Settings className="w-3.5 h-3.5" />
              </div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Question Settings</h4>
            </div>
            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors">
                <input type="checkbox" checked={selectedElementData.required || false} onChange={(e) => handleRequiredChange(e.target.checked)} className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                <span className="text-sm text-gray-700">Required</span>
              </label>
              {!hideMultiSelect && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors">
                  <input type="checkbox" checked={selectedElementData.multiSelect || false} onChange={(e) => handleMultiSelectChange(e.target.checked)} className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                  <span className="text-sm text-gray-700">Allow Multiple Selection</span>
                </label>
              )}
            </div>
          </div>
        );
      })()}

      {/* Body Map Layout Editor */}
      {selectedElementData.type === 'question' && selectedElementData.data?.type === 'body_map' && (() => {
        const overrides = selectedElementData.responseConfig?.regionOverrides || {};
        const editMode = !!selectedElementData.responseConfig?.bodyMapEditMode;

        const resetAll = () => {
          handleResponseConfigChange('regionOverrides', {});
          handleResponseConfigChange('bodyMapActiveSlug', null);
          handleResponseConfigChange('selectedPanelPosition', null);
        };

        // Per-region edit helpers disabled — kept for reference if region
        // editing is ever re-enabled.
        // const activeSlug = selectedElementData.responseConfig?.bodyMapActiveSlug || null;
        // const activeOverride = activeSlug ? overrides[activeSlug] : null;
        // const defaultShape = activeSlug ? getDefaultShape(activeSlug) : null;
        // const effective = activeSlug
        //   ? { ...(defaultShape || {}), ...(activeOverride || {}) }
        //   : null;
        //
        // const updateActiveRegion = (patch) => {
        //   if (!activeSlug) return;
        //   const nextRegion = { ...(overrides[activeSlug] || {}), ...patch };
        //   handleResponseConfigChange('regionOverrides', { ...overrides, [activeSlug]: nextRegion });
        // };
        //
        // const resetActiveRegion = () => {
        //   if (!activeSlug) return;
        //   const next = { ...overrides };
        //   delete next[activeSlug];
        //   handleResponseConfigChange('regionOverrides', next);
        // };
        //
        // const sizeFields = (() => {
        //   if (!effective) return [];
        //   switch (effective.type) {
        //     case 'circle':
        //       return [{ key: 'r', label: 'Radius' }];
        //     case 'rect':
        //       return [
        //         { key: 'w', label: 'Width' },
        //         { key: 'h', label: 'Height' },
        //       ];
        //     case 'ellipse':
        //       return [
        //         { key: 'rx', label: 'Width' },
        //         { key: 'ry', label: 'Height' },
        //       ];
        //     default:
        //       return [];
        //   }
        // })();

        return (
          <div className="bg-gradient-to-br from-white dark:from-gray-800 to-gray-50 dark:to-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
                  <Settings className="w-3.5 h-3.5" />
                </div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Body Map Layout</h4>
              </div>
              <button
                type="button"
                onClick={() => handleResponseConfigChange('bodyMapEditMode', !editMode)}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors ${
                  editMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {editMode ? 'Exit Edit Mode' : 'Edit Layout'}
              </button>
            </div>

            {editMode ? (
              <div className="space-y-3">
                <p className="text-[11px] text-gray-500">
                  Drag the <strong>Selected Areas</strong> panel by its header to move it.
                </p>
                {/* Per-region edit UI disabled — individual body regions are no
                    longer draggable/resizable. Reset button retained below in
                    case existing overrides need to be cleared. */}
                {/* {activeSlug ? (
                  <div className="border rounded-lg p-3 bg-blue-50/40">
                    <div className="text-xs font-semibold text-blue-800 mb-2">{activeSlug}</div>
                    <div className="grid grid-cols-2 gap-2">
                      {effective?.cx !== undefined && (
                        <label className="text-[10px] text-gray-600 block">X
                          <NumericInput value={effective.cx} fallback={0} onChange={(v) => updateActiveRegion({ cx: v })} />
                        </label>
                      )}
                      {effective?.cy !== undefined && (
                        <label className="text-[10px] text-gray-600 block">Y
                          <NumericInput value={effective.cy} fallback={0} onChange={(v) => updateActiveRegion({ cy: v })} />
                        </label>
                      )}
                      {effective?.x !== undefined && (
                        <label className="text-[10px] text-gray-600 block">X
                          <NumericInput value={effective.x} fallback={0} onChange={(v) => updateActiveRegion({ x: v })} />
                        </label>
                      )}
                      {effective?.y !== undefined && (
                        <label className="text-[10px] text-gray-600 block">Y
                          <NumericInput value={effective.y} fallback={0} onChange={(v) => updateActiveRegion({ y: v })} />
                        </label>
                      )}
                      {sizeFields.map((f) => (
                        <label key={f.key} className="text-[10px] text-gray-600 block">{f.label}
                          <NumericInput
                            value={effective[f.key]}
                            fallback={0}
                            min={1}
                            onChange={(v) => updateActiveRegion({ [f.key]: v })}
                          />
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={resetActiveRegion}
                      className="mt-3 text-[11px] text-gray-600 hover:text-gray-900 underline"
                    >
                      Reset this region to default
                    </button>
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-400 italic">No region selected. Click one on the canvas.</p>
                )} */}
                <button
                  type="button"
                  onClick={resetAll}
                  className="w-full px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Reset entire layout
                </button>
              </div>
            ) : (
              <p className="text-[11px] text-gray-500">
                Toggle Edit Layout to reposition the <strong>Selected Areas</strong> panel.
                {Object.keys(overrides).length > 0 && ` (${Object.keys(overrides).length} region${Object.keys(overrides).length === 1 ? '' : 's'} customized)`}
              </p>
            )}
          </div>
        );
      })()}

      {/* Content & Formatting - Hidden for library items */}
      {(selectedElementData.type === 'text' || selectedElementData.type === 'button' || (selectedElementData.type === 'question' && !selectedElementData.isLibraryItem)) && (
        <div className="bg-gradient-to-br from-white dark:from-gray-800 to-gray-50 dark:to-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1.5 bg-purple-100 text-purple-600 rounded-lg">
              <Edit2 className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              {selectedElementData.type === 'text' ? 'Content' :
               selectedElementData.type === 'button' ? 'Button Text' : 'Question Text'}
            </h4>
          </div>
          <RichTextEditor
            value={selectedElementData.type === 'text'
              ? selectedElementData.content
              : selectedElementData.type === 'button'
              ? (selectedElementData.content || selectedElementData.label || '')
              : (selectedElementData.content || selectedElementData.itemData?.text || '')
            }
            onChange={(html) => updateElementContent(selectedElement, html)}
            label={selectedElementData.type === 'text' ? 'Content' :
                   selectedElementData.type === 'button' ? 'Button Text' : 'Question Text'}
            placeholder={selectedElementData.type === 'text'
              ? 'Enter text content...'
              : selectedElementData.type === 'button'
              ? 'Enter button text...'
              : 'Enter question text...'
            }
            rows={3}
          />
        </div>
      )}

      {showTypographyControls && (
        <div className="bg-gradient-to-br from-white dark:from-gray-800 to-indigo-50/30 rounded-xl p-4 border border-indigo-200 shadow-sm dark:shadow-gray-900/20">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1.5 bg-indigo-100 text-indigo-600 rounded-lg">
              <Type className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Typography &amp; Formatting</h4>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Font Family</label>
              <select
                value={selectedElementData.style?.fontFamily || 'Arial, sans-serif'}
                onChange={(e) => updateElementStyle('fontFamily', e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all"
              >
                {FONT_FAMILY_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-0.5 uppercase tracking-wide">Font Size</label>
                <p className="text-[9px] text-gray-400 mb-1">Question stem text size</p>
                <div className="flex items-center gap-1">
                  <NumericInput
                    min={8}
                    max={72}
                    value={selectedElementData.style?.fontSize ?? 16}
                    fallback={16}
                    onChange={(val) => updateElementStyle('fontSize', val)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all"
                  />
                  <span className="text-[10px] text-gray-500">px</span>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-0.5 uppercase tracking-wide">Line Height</label>
                <p className="text-[9px] text-gray-400 mb-1">Multiplier (1.0 = tight, 2.0 = double)</p>
                <NumericInput
                  float
                  min={1}
                  max={3}
                  step="0.1"
                  fallback={1.5}
                  value={selectedElementData.style?.lineHeight ?? 1.5}
                  onChange={(val) => updateElementStyle('lineHeight', val)}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-2 uppercase tracking-wide">Font Weight</label>
              <div className="grid grid-cols-2 gap-2">
                {(() => {
                  const currentFont = selectedElementData.style?.fontFamily || 'Arial, sans-serif';
                  const supported = FONT_WEIGHT_SUPPORT[currentFont] || FONT_WEIGHT_OPTIONS.map(o => o.value);
                  return FONT_WEIGHT_OPTIONS
                    .filter(option => supported.includes(option.value))
                    .map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateElementStyle('fontWeight', option.value)}
                        className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                          String(selectedElementData.style?.fontWeight || '400') === option.value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ));
                })()}
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-2 uppercase tracking-wide">
                {selectedElementData.type === 'question' ? 'Question Text Alignment' : 'Text Alignment'}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {TEXT_ALIGNMENT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updateElementStyle('textAlign', option.value)}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-all ${
                        (selectedElementData.style?.textAlign || 'left') === option.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Response Options Alignment — only for question elements */}
            {selectedElementData.type === 'question' && (
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-2 uppercase tracking-wide">Response Options Alignment</label>
                <div className="grid grid-cols-3 gap-2">
                  {TEXT_ALIGNMENT_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const current = selectedElementData.responseConfig?.optionsAlignment ?? 'left';
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateOptionsAlignment(option.value)}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-all ${
                          current === option.value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Text Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElementData.style?.color || '#111827'}
                  onChange={(e) => updateElementStyle('color', e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border border-gray-200 dark:border-gray-700"
                />
                <input
                  type="text"
                  value={selectedElementData.style?.color || '#111827'}
                  onChange={(e) => updateElementStyle('color', e.target.value)}
                  className="flex-1 px-3 py-2 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all"
                  placeholder="#111827"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Response Options - Hidden for library items and types with fixed option sets (body_map) */}
      {selectedElementData.type === 'question' && !selectedElementData.isLibraryItem && selectedElementData.data?.type !== 'body_map' && (
        <div className="bg-gradient-to-br from-white dark:from-gray-800 to-primary-50/30 rounded-lg p-4 border-2 border-primary-100 shadow-sm dark:shadow-gray-900/20 overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs font-bold text-primary-800 uppercase tracking-wide">
                <List className="w-4 h-4" />
                Response Options
              </label>
              {(selectedElementData.itemData?.options || selectedElementData.itemData?.response_options) && (
                <span className="px-2 py-1 text-[9px] font-semibold bg-blue-100 text-blue-700 rounded-md border border-blue-200">
                  Imported
                </span>
              )}
              {(selectedElementData.responseOptions?.length || 0) > 0 && (
                <span className="px-2 py-1 text-[9px] font-semibold bg-primary-100 text-primary-700 rounded-md border border-primary-200">
                  {selectedElementData.responseOptions.length} {selectedElementData.responseOptions.length === 1 ? 'option' : 'options'}
                </span>
              )}
            </div>
            <button onClick={handleAddOption} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-lg font-semibold shadow-sm dark:shadow-gray-900/20 hover:shadow transition-all">
              <Plus className="w-3 h-3" />
              Add Option
            </button>
          </div>
          <div className="mb-4">
            <label className="block text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Quick Fill</label>
            <select className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border-2 border-primary-200 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all font-medium text-gray-700" onChange={handlePresetChange}>
              <option value="">-- Select Preset --</option>
              <option value="phq9">PHQ-9 Scale (0-3)</option>
              <option value="gad7">GAD-7 Scale (0-3)</option>
              <option value="nrs">NRS 0-10</option>
              <option value="severity">Severity (None to Severe)</option>
              <option value="likert5">Ordinal 5-Point</option>
              <option value="yesno">Yes/No</option>
            </select>
          </div>
          <div className="border-2 border-primary-100 rounded-lg overflow-x-auto overflow-y-visible shadow-sm dark:shadow-gray-900/20 relative scroll-smooth">
            <table className="w-full text-xs min-w-[400px]">
              <thead className="bg-gradient-to-r from-primary-50 to-blue-50 border-b-2 border-primary-200">
                <tr>
                  <th className="px-2 py-2.5 text-left text-[10px] font-bold text-primary-800 uppercase tracking-wide w-10 min-w-[40px]">#</th>
                  <th className="px-2 py-2.5 text-left text-[10px] font-bold text-primary-800 uppercase tracking-wide min-w-[180px]">Label</th>
                  <th className="px-2 py-2.5 text-left text-[10px] font-bold text-primary-800 uppercase tracking-wide w-20 min-w-[80px]">Value</th>
                  <th className="px-2 py-2.5 text-center text-[10px] font-bold text-primary-800 uppercase tracking-wide w-12 min-w-[48px]"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800">
                {(selectedElementData.responseOptions || []).map((option, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-primary-50/30 transition-colors group">
                    <td className="px-2 py-2.5">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 font-semibold text-[10px]">{option.order}</div>
                    </td>
                    <td className="px-2 py-2">
                      <RichTextEditor value={option.label} onChange={(html) => handleOptionLabelChange(index, html)} compact={true} />
                    </td>
                    <td className="px-2 py-2">
                      <NumericInput value={option.value} fallback={0} onChange={(val) => handleOptionValueChange(index, val)} className="w-full px-2 py-1.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:bg-white dark:focus:bg-gray-800 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 rounded-md text-center font-medium text-gray-700 transition-all" />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => handleDeleteOption(index)} className="w-7 h-7 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-all" title="Delete option">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {(!selectedElementData.responseOptions || selectedElementData.responseOptions.length === 0) && (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <List className="w-8 h-8 text-gray-300 mb-2" />
                        <p className="text-sm text-gray-500 font-medium">No response options yet</p>
                        <p className="text-[10px] text-gray-400 mt-1">Click "+ Add Option" to define choices.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-start gap-1.5 mt-2 px-1">
            <div className="w-4 h-4 rounded bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-primary-600 text-[10px]">i</span>
            </div>
            <p className="text-[10px] text-gray-600 leading-relaxed">
              <span className="font-semibold">Values</span> are used for scoring calculations and skip logic evaluations
            </p>
          </div>
        </div>
      )}

      {/* Response Display (for questions with response options, or scale/slider types without options) */}
      {selectedElementData.type === 'question' && (() => {
        const qType = selectedElementData.data?.type || selectedElementData.data?.response_type;
        const qLayout = selectedElementData.responseConfig?.layout;
        const isScaleType = ['vas', 'nrs', 'numeric'].includes(qType) ||
          ['vas', 'nrs', 'vrs-unipolar', 'vrs-bipolar'].includes(qLayout);
        return selectedElementData.responseOptions?.length > 0 || isScaleType;
      })() && (
        <div className="bg-gradient-to-br from-white dark:from-gray-800 to-blue-50/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
          <div className="flex items-center space-x-2 mb-4">
            <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
              <Layout className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Response Layout</h4>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Layout Style</label>
              {(() => {
                const questionType = selectedElementData.data?.type || selectedElementData.data?.response_type || 'ordinal';
                const handleLayoutChange = (e) => {
                  const newLayout = e.target.value;
                  const updatedConfig = { ...selectedElementData.responseConfig, layout: newLayout };
                  const newHeight = calculateQuestionHeight(selectedElementData.data, updatedConfig);
                  const updatedScreens = screens.map((screen, idx) => {
                    if (idx === currentScreenIndex) {
                      return {
                        ...screen,
                        elements: screen.elements.map(el =>
                          el.id === selectedElement
                            ? { ...el, responseConfig: updatedConfig, size: { ...el.size, height: newHeight } }
                            : el
                        )
                      };
                    }
                    return screen;
                  });
                  setScreens(updatedScreens);
                };
                return (
                  <select
                    value={
                      (questionType === 'nrs' || questionType === 'numeric') && (!selectedElementData.responseConfig?.layout || selectedElementData.responseConfig?.layout === 'text' || selectedElementData.responseConfig?.layout === 'vertical')
                        ? 'nrs'
                        : selectedElementData.responseConfig?.layout || 'vertical'
                    }
                    onChange={handleLayoutChange}
                    className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  >
                    {questionType === 'vas' ? (
                      <option value="vas">VAS - Visual Analog Scale (Slider)</option>
                    ) : questionType === 'body_map' ? (
                      <option value="body_map">Body Map (Front + Back)</option>
                    ) : questionType === 'nrs' || questionType === 'numeric' ? (
                      <optgroup label="Numeric Scale Layouts">
                        <option value="nrs">NRS - Numeric Rating Scale</option>
                        <option value="thermometer">Thermometer Scale</option>
                      </optgroup>
                    ) : questionType === 'text' || questionType === 'free_text' ? (
                      <option value="textarea">Text Area</option>
                    ) : questionType === 'dropdown' ? (
                      <option value="dropdown">Dropdown Select</option>
                    ) : questionType === 'yes_no' || questionType === 'boolean' ? (
                      <optgroup label="Yes/No Layouts">
                        <option value="horizontal">Horizontal Buttons</option>
                        <option value="vertical">Vertical List</option>
                      </optgroup>
                    ) : questionType === 'ordinal' || questionType === 'likert' ? (
                      <>
                        <optgroup label="Standard">
                          <option value="vertical">Vertical List</option>
                          <option value="horizontal">Horizontal Buttons</option>
                          <option value="grid">Grid Layout</option>
                        </optgroup>
                        {user?.is_superuser && (
                          <optgroup label="Clinical Scales">
                            <option value="segmented">Segmented Control</option>
                            <option value="emoji">Emoji / Face Scale</option>
                            <option value="stepped-slider">Stepped Slider</option>
                            <option value="thermometer">Thermometer Scale</option>
                          </optgroup>
                        )}
                      </>
                    ) : questionType === 'rating' ? (
                      <>
                        <optgroup label="Scale Layouts">
                          <option value="vas">VAS - Visual Analog Scale (Slider)</option>
                          <option value="nrs">NRS - Numeric Rating Scale (0-10)</option>
                          <option value="vrs-unipolar">VRS - Verbal Rating Scale (Unipolar)</option>
                          <option value="vrs-bipolar">VRS - Verbal Rating Scale (Bipolar)</option>
                        </optgroup>
                        <optgroup label="Button Layouts">
                          <option value="horizontal">Horizontal Buttons</option>
                          <option value="vertical">Vertical List</option>
                        </optgroup>
                      </>
                    ) : questionType === 'checkbox' ? (
                      <optgroup label="Checkbox Layouts">
                        <option value="vertical">Vertical List</option>
                        <option value="grid">Grid Layout</option>
                      </optgroup>
                    ) : (
                      <optgroup label="Standard Layouts">
                        <option value="vertical">Vertical List</option>
                        <option value="horizontal">Horizontal Buttons</option>
                        <option value="grid">Grid Layout</option>
                      </optgroup>
                    )}
                  </select>
                );
              })()}
            </div>

            {selectedElementData.responseConfig?.layout === 'grid' && (
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Grid Columns</label>
                <select
                  value={selectedElementData.responseConfig?.gridColumns || 2}
                  onChange={(e) => handleResponseConfigChange('gridColumns', parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                >
                  <option value={2}>2 Columns</option>
                  <option value={3}>3 Columns</option>
                  <option value={4}>4 Columns</option>
                </select>
              </div>
            )}

            {/* VAS Configuration */}
            {selectedElementData.responseConfig?.layout === 'vas' && (
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="text-[10px] font-semibold text-blue-600 uppercase">VAS Settings</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1">Left Anchor</label>
                    <input type="text" value={selectedElementData.responseConfig?.leftAnchor || ''} onChange={(e) => handleResponseConfigChange('leftAnchor', e.target.value)} placeholder="e.g., No pain" className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1">Right Anchor</label>
                    <input type="text" value={selectedElementData.responseConfig?.rightAnchor || ''} onChange={(e) => handleResponseConfigChange('rightAnchor', e.target.value)} placeholder="e.g., Worst pain" className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={selectedElementData.responseConfig?.showCurrentValue || false} onChange={(e) => handleResponseConfigChange('showCurrentValue', e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="text-[10px] text-gray-600 dark:text-gray-300">Show selected value under slider</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={selectedElementData.responseConfig?.colorGradient || false} onChange={(e) => handleResponseConfigChange('colorGradient', e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="text-[10px] text-gray-600 dark:text-gray-300">Color gradient (green to red)</span>
                </label>
              </div>
            )}

            {/* Numeric Scale Configuration — shared by NRS and Thermometer.
                Both need min/max and anchor labels; orientation and color
                gradient are NRS-only (thermometer is always vertical and uses
                a single fillColor). */}
            {(selectedElementData.responseConfig?.layout === 'nrs' || selectedElementData.responseConfig?.layout === 'thermometer' || selectedElementData.data?.type === 'numeric') && (() => {
              const isThermometer = selectedElementData.responseConfig?.layout === 'thermometer';
              return (
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="text-[10px] font-semibold text-blue-600 uppercase">
                  {isThermometer ? 'Thermometer Settings' : 'NRS Settings'}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Min Value</label>
                    <NumericInput value={selectedElementData.responseConfig?.minValue ?? 0} fallback={0} onChange={(val) => handleResponseConfigChange('minValue', val)} />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Max Value</label>
                    <NumericInput value={selectedElementData.responseConfig?.maxValue ?? 10} fallback={10} onChange={(val) => handleResponseConfigChange('maxValue', val)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">{isThermometer ? 'Bottom Anchor' : 'Left Anchor'}</label>
                    <input type="text" value={selectedElementData.responseConfig?.leftAnchor || ''} onChange={(e) => handleResponseConfigChange('leftAnchor', e.target.value)} placeholder={isThermometer ? 'e.g., Worst' : 'e.g., No pain'} className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">{isThermometer ? 'Top Anchor' : 'Right Anchor'}</label>
                    <input type="text" value={selectedElementData.responseConfig?.rightAnchor || ''} onChange={(e) => handleResponseConfigChange('rightAnchor', e.target.value)} placeholder={isThermometer ? 'e.g., Best' : 'e.g., Worst pain'} className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400" />
                  </div>
                </div>
                {isThermometer ? (
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Step</label>
                    <NumericInput value={selectedElementData.responseConfig?.step ?? 10} fallback={10} onChange={(val) => handleResponseConfigChange('step', val)} />
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Orientation</label>
                      <select value={selectedElementData.responseConfig?.orientation || 'horizontal'} onChange={(e) => handleResponseConfigChange('orientation', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400">
                        <option value="horizontal">Horizontal</option>
                        <option value="vertical">Vertical</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={selectedElementData.responseConfig?.colorGradient || false} onChange={(e) => handleResponseConfigChange('colorGradient', e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                      <span className="text-[10px] text-gray-600 dark:text-gray-300">Color gradient (green to red)</span>
                    </label>
                  </>
                )}
              </div>
              );
            })()}

            {/* VRS Configuration */}
            {(selectedElementData.responseConfig?.layout === 'vrs-unipolar' || selectedElementData.responseConfig?.layout === 'vrs-bipolar') && (
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="text-[10px] font-semibold text-blue-600 uppercase">VRS Settings</div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Orientation</label>
                  <select value={selectedElementData.responseConfig?.orientation || 'vertical'} onChange={(e) => {
                    const updatedConfig = { ...selectedElementData.responseConfig, orientation: e.target.value };
                    const newHeight = calculateQuestionHeight(selectedElementData.data, updatedConfig);
                    const updatedScreens = screens.map((screen, idx) => {
                      if (idx === currentScreenIndex) {
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, responseConfig: updatedConfig, size: { ...el.size, height: newHeight } } : el) };
                      }
                      return screen;
                    });
                    setScreens(updatedScreens);
                  }} className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400">
                    <option value="vertical">Vertical</option>
                    <option value="horizontal">Horizontal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Button Spacing (px)</label>
                  <NumericInput value={selectedElementData.responseConfig?.spacing || 12} fallback={12} min={0} onChange={(val) => handleResponseConfigChange('spacing', val)} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Button Height (px)</label>
                    <NumericInput value={selectedElementData.responseConfig?.vrsButtonHeight || 48} fallback={48} min={20} onChange={(val) => handleResponseConfigChange('vrsButtonHeight', val)} />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Min Width (px)</label>
                    <NumericInput value={selectedElementData.responseConfig?.vrsButtonMinWidth || 200} fallback={200} min={50} onChange={(val) => handleResponseConfigChange('vrsButtonMinWidth', val)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Selected Color</label>
                    <input type="color" value={selectedElementData.responseConfig?.vrsSelectedColor || '#0d9488'} onChange={(e) => handleResponseConfigChange('vrsSelectedColor', e.target.value)} className="w-full h-8 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Unselected Color</label>
                    <input type="color" value={selectedElementData.responseConfig?.vrsUnselectedColor || '#ffffff'} onChange={(e) => handleResponseConfigChange('vrsUnselectedColor', e.target.value)} className="w-full h-8 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                  </div>
                </div>
              </div>
            )}

            {/* Option Appearance — for standard layouts (vertical, horizontal, grid).
                Suppressed for numeric-type questions since they render as the NRS
                scale regardless of responseConfig.layout, so these controls
                (gap / equal-width / font-size / padding) would have no visible
                effect and confuse the author. */}
            {['vertical', 'horizontal', 'grid'].includes(selectedElementData.responseConfig?.layout) && selectedElementData.data?.type !== 'numeric' && (
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="text-[10px] font-semibold text-blue-600 uppercase">Option Appearance</div>

                {/* Display mode: radio/checkbox vs buttons */}
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Display As</label>
                  <select
                    value={
                      selectedElementData.responseConfig?.buttonStyle
                        ? selectedElementData.responseConfig.buttonStyle
                        : 'radio'
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'radio') {
                        handleResponseConfigChange('buttonStyle', null);
                        return;
                      }
                      const defaults = val === 'filled'
                        ? { btnBgColor: '#0d9488', btnTextColor: '#ffffff', btnBorderColor: '#0d9488' }
                        : { btnBgColor: '#ffffff', btnTextColor: '#0d9488', btnBorderColor: '#0d9488' };
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return {
                          ...screen,
                          elements: screen.elements.map(el =>
                            el.id === selectedElement
                              ? { ...el, responseConfig: { ...el.responseConfig, buttonStyle: val, ...defaults } }
                              : el
                          )
                        };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400"
                  >
                    <option value="radio">Radio Buttons</option>
                    <option value="outline">Outlined Buttons</option>
                    <option value="filled">Filled Buttons</option>
                  </select>
                </div>

                {/* Equal width toggle — only for button modes or vertical layout */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedElementData.responseConfig?.equalWidth || false}
                    onChange={(e) => handleResponseConfigChange('equalWidth', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-[10px] text-gray-600">Equal width options</span>
                </label>

                {/* Option spacing */}
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Gap Between Options (px)</label>
                  <p className="text-[9px] text-gray-400 mb-1">Vertical space between each option row</p>
                  <NumericInput
                    min={0}
                    max={32}
                    value={selectedElementData.responseConfig?.spacing ?? 8}
                    fallback={8}
                    onChange={(val) => handleResponseConfigChange('spacing', val)}
                  />
                </div>

                {/* Option font size + padding — always visible */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">Option Font Size (px)</label>
                    <p className="text-[9px] text-gray-400 mb-1">Response option text size</p>
                    <NumericInput
                      min={8}
                      max={32}
                      value={selectedElementData.responseConfig?.optionFontSize ?? 14}
                      fallback={14}
                      onChange={(val) => handleResponseConfigChange('optionFontSize', val)}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">Inner Padding (px)</label>
                    <p className="text-[9px] text-gray-400 mb-1">Space inside each option</p>
                    <NumericInput
                      min={2}
                      max={24}
                      value={selectedElementData.responseConfig?.optionPadding ?? 8}
                      fallback={8}
                      onChange={(val) => handleResponseConfigChange('optionPadding', val)}
                    />
                  </div>
                </div>

                {/* Radio/Checkbox size — only when not using button style */}
                {showRadioSizeControl && (
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">Radio / Checkbox Size (px)</label>
                    <p className="text-[9px] text-gray-400 mb-1">Diameter of the indicator dot/box</p>
                    <NumericInput
                      min={12}
                      max={32}
                      value={selectedElementData.responseConfig?.radioSize ?? 16}
                      fallback={16}
                      onChange={(val) => handleResponseConfigChange('radioSize', val)}
                    />
                  </div>
                )}

                {/* Button styling controls — only when using button display modes */}
                {usesButtonOptions && (
                  <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-[10px] font-semibold text-blue-600 uppercase">Button Styling</div>

                    {/* Box width and text alignment */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Box Width (px)</label>
                        <p className="text-[9px] text-gray-400 mb-1">Use 0 for automatic width</p>
                        <NumericInput
                          min={0}
                          max={600}
                          value={selectedElementData.responseConfig?.optionBoxWidth ?? 0}
                          fallback={0}
                          onChange={(val) => handleResponseConfigChange('optionBoxWidth', val)}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Text Align</label>
                        <p className="text-[9px] text-gray-400 mb-1">Inside each option box</p>
                        <select
                          value={selectedElementData.responseConfig?.optionTextAlign || 'center'}
                          onChange={(e) => handleResponseConfigChange('optionTextAlign', e.target.value)}
                          className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400"
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    </div>

                    {/* Box height — pairs with Box Width above */}
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Box Height (px)</label>
                      <p className="text-[9px] text-gray-400 mb-1">Use 0 for automatic height. Sets a fixed height and centers the label vertically.</p>
                      <NumericInput
                        min={0}
                        max={200}
                        value={selectedElementData.responseConfig?.optionBoxHeight ?? 0}
                        fallback={0}
                        onChange={(val) => handleResponseConfigChange('optionBoxHeight', val)}
                      />
                    </div>

                    {/* Border radius and width */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Border Radius (px)</label>
                        <NumericInput
                          min={0}
                          max={24}
                          value={selectedElementData.responseConfig?.btnBorderRadius ?? 4}
                          fallback={4}
                          onChange={(val) => handleResponseConfigChange('btnBorderRadius', val)}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Border Width (px)</label>
                        <NumericInput
                          min={1}
                          max={4}
                          value={selectedElementData.responseConfig?.btnBorderWidth ?? 1}
                          fallback={1}
                          onChange={(val) => handleResponseConfigChange('btnBorderWidth', val)}
                        />
                      </div>
                    </div>

                    {/* Unselected colors */}
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Unselected State</label>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-[8px] text-gray-400 mb-0.5">Background</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnBgColor || '#ffffff'} onChange={(e) => handleResponseConfigChange('btnBgColor', e.target.value)} className="w-full h-7 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                        </div>
                        <div>
                          <label className="block text-[8px] text-gray-400 mb-0.5">Text</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnTextColor || '#374151'} onChange={(e) => handleResponseConfigChange('btnTextColor', e.target.value)} className="w-full h-7 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                        </div>
                        <div>
                          <label className="block text-[8px] text-gray-400 mb-0.5">Border</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnBorderColor || '#d1d5db'} onChange={(e) => handleResponseConfigChange('btnBorderColor', e.target.value)} className="w-full h-7 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                        </div>
                      </div>
                    </div>

                    {/* Selected colors */}
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Selected State</label>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-[8px] text-gray-400 mb-0.5">Background</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnSelectedBgColor || '#0d9488'} onChange={(e) => handleResponseConfigChange('btnSelectedBgColor', e.target.value)} className="w-full h-7 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                        </div>
                        <div>
                          <label className="block text-[8px] text-gray-400 mb-0.5">Text</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnSelectedTextColor || '#ffffff'} onChange={(e) => handleResponseConfigChange('btnSelectedTextColor', e.target.value)} className="w-full h-7 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                        </div>
                        <div>
                          <label className="block text-[8px] text-gray-400 mb-0.5">Border</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnSelectedBorderColor || '#0d9488'} onChange={(e) => handleResponseConfigChange('btnSelectedBorderColor', e.target.value)} className="w-full h-7 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                        </div>
                      </div>
                    </div>

                    {/* Font weight */}
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Font Weight</label>
                      <select
                        value={selectedElementData.responseConfig?.btnFontWeight || '400'}
                        onChange={(e) => handleResponseConfigChange('btnFontWeight', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400"
                      >
                        <option value="300">Light</option>
                        <option value="400">Normal</option>
                        <option value="500">Medium</option>
                        <option value="600">Semibold</option>
                        <option value="700">Bold</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Free Position Mode — ungroup/regroup toggle */}
            {ungroupQuestion && regroupQuestion && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
                {selectedElementData.isUngrouped ? (
                  <>
                    <button
                      onClick={() => regroupQuestion(selectedElement)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                      <Layers className="w-3.5 h-3.5" />
                      Exit Free Position
                    </button>

                    {(() => {
                      const pad = selectedElementData.style?.padding || 0;
                      const parentW = selectedElementData.size.width - pad * 2;
                      const parentH = selectedElementData.size.height - pad * 2;
                      const textSub = selectedElementData.subElements?.find(s => s.type === 'question-text');
                      const options = selectedElementData.subElements?.filter(s => s.type === 'question-option') || [];
                      const spacing = selectedElementData.responseConfig?.spacing || 8;
                      const optFontSize = selectedElementData.responseConfig?.optionFontSize || 14;
                      const optPadding = selectedElementData.responseConfig?.optionPadding || 8;
                      const optH = optFontSize + optPadding * 2 + 8;
                      const longestLabel = options.length > 0 ? Math.max(...options.map(o => (o.content || '').length)) : 5;
                      const estOptW = Math.min(parentW, longestLabel * optFontSize * 0.65 + 50);

                      const applySubElements = (updated) => {
                        const updatedScreens = screens.map((screen, idx) => {
                          if (idx !== currentScreenIndex) return screen;
                          return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, subElements: updated } : el) };
                        });
                        setScreens(updatedScreens);
                      };

                      const alignOptionsToAnchor = (anchorX, anchorY) => {
                        if (options.length === 0) return;
                        // Compute bounding box of options as they currently are
                        const minX = Math.min(...options.map(o => o.position.x));
                        const minY = Math.min(...options.map(o => o.position.y));
                        const maxX = Math.max(...options.map(o => o.position.x + (o.content || '').length * optFontSize * 0.7 + 50));
                        const maxY = Math.max(...options.map(o => o.position.y + optH));
                        const groupW = maxX - minX;
                        const groupH = maxY - minY;

                        // Target position for the group's top-left corner
                        const targetX = anchorX === 'left' ? 0 : anchorX === 'center' ? (parentW - groupW) / 2 : parentW - groupW;
                        const targetY = anchorY === 'top' ? 0 : anchorY === 'middle' ? (parentH - groupH) / 2 : parentH - groupH;

                        // Translate all options by the delta
                        const dx = targetX - minX;
                        const dy = targetY - minY;
                        const updated = selectedElementData.subElements.map(sub => {
                          if (sub.type !== 'question-option') return sub;
                          return { ...sub, position: { x: Math.max(0, Math.round(sub.position.x + dx)), y: Math.max(0, Math.round(sub.position.y + dy)) } };
                        });
                        applySubElements(updated);
                      };

                      const cellClass = "w-6 h-6 flex items-center justify-center rounded border border-gray-200 dark:border-gray-700 hover:bg-purple-100 hover:border-purple-400 cursor-pointer transition-colors group";

                      return (
                        <>
                          {/* Question Text position */}
                          {textSub && (
                            <div className="mt-2">
                              <div className="text-[10px] font-semibold text-purple-600 uppercase mb-1">Question Text</div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-gray-400 w-4">X</span>
                                <NumericInput value={Math.round(textSub.position.x)} fallback={0} onChange={(val) => updateSubElementPosition(selectedElement, textSub.id, { ...textSub.position, x: val })} className="w-14 px-1 py-0.5 text-[10px] border border-gray-200 dark:border-gray-700 rounded text-center" />
                                <span className="text-[9px] text-gray-400 w-4">Y</span>
                                <NumericInput value={Math.round(textSub.position.y)} fallback={0} onChange={(val) => updateSubElementPosition(selectedElement, textSub.id, { ...textSub.position, y: val })} className="w-14 px-1 py-0.5 text-[10px] border border-gray-200 dark:border-gray-700 rounded text-center" />
                              </div>
                            </div>
                          )}

                          {/* Response options alignment */}
                          {options.length > 0 && (
                            <div className="mt-2">
                              <div className="text-[10px] font-semibold text-purple-600 uppercase mb-1">Align Response Options</div>
                              <div className="flex items-start gap-3">
                                <div className="grid grid-cols-3 gap-0.5 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                                  {['top','middle','bottom'].map(row =>
                                    ['left','center','right'].map(col => (
                                      <button key={`${row}-${col}`} onClick={() => alignOptionsToAnchor(col, row)} className={cellClass} title={`${row} ${col}`}>
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 group-hover:bg-purple-600" />
                                      </button>
                                    ))
                                  )}
                                </div>
                                <div className="flex flex-col gap-1.5 flex-1">
                                  <button onClick={() => distributeOptions(selectedElement, 'vertical')} className="flex items-center gap-1.5 px-2 py-1.5 text-[9px] text-gray-600 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-purple-50 hover:border-purple-300 transition-colors">
                                    <ArrowUpDown className="w-3 h-3 flex-shrink-0" /> Stack Vertical
                                  </button>
                                  <button onClick={() => distributeOptions(selectedElement, 'horizontal')} className="flex items-center gap-1.5 px-2 py-1.5 text-[9px] text-gray-600 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-purple-50 hover:border-purple-300 transition-colors">
                                    <ArrowLeftRight className="w-3 h-3 flex-shrink-0" /> Stack Horizontal
                                  </button>
                                  <button onClick={() => distributeOptions(selectedElement, 'grid')} className="flex items-center gap-1.5 px-2 py-1.5 text-[9px] text-gray-600 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-purple-50 hover:border-purple-300 transition-colors">
                                    <Layout className="w-3 h-3 flex-shrink-0" /> Grid (2 col)
                                  </button>
                                </div>
                              </div>

                              {/* Per-option position list */}
                              <div className="mt-2">
                                <div className="text-[10px] font-semibold text-purple-600 uppercase mb-1">Option Positions</div>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                  {options.map((sub) => (
                                    <div key={sub.id} className="flex items-center gap-1.5 text-[10px]">
                                      <span className="w-16 truncate text-gray-500" title={sub.content}>{sub.content}</span>
                                      <NumericInput value={Math.round(sub.position.x)} fallback={0} onChange={(val) => updateSubElementPosition(selectedElement, sub.id, { ...sub.position, x: val })} className="w-12 px-1 py-0.5 text-[10px] border border-gray-200 rounded text-center" title="X" />
                                      <NumericInput value={Math.round(sub.position.y)} fallback={0} onChange={(val) => updateSubElementPosition(selectedElement, sub.id, { ...sub.position, y: val })} className="w-12 px-1 py-0.5 text-[10px] border border-gray-200 rounded text-center" title="Y" />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <button
                    onClick={() => ungroupQuestion(selectedElement)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <MousePointer className="w-3.5 h-3.5" />
                    Custom Layout
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Element Code - For input elements */}
      {isInputElement && (
        <div className="bg-gradient-to-br from-white dark:from-gray-800 to-blue-50/30 rounded-xl p-4 border border-blue-200 shadow-sm dark:shadow-gray-900/20">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
              <Type className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Element Identifier</h4>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Code / Variable Name</label>
              <input
                type="text"
                value={selectedElementData.code || ''}
                onChange={(e) => handleElementCodeChange(e.target.value)}
                placeholder="e.g., pain_level, comments"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
              />
              <p className="text-[10px] text-gray-500 mt-1.5">
                <span className="font-semibold text-blue-600">✓ This is what shows in exports.</span> Leave blank to use the internal ID below.
              </p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
              <label className="block text-[10px] text-gray-400 mb-0.5 uppercase tracking-wide">Internal Element ID (read-only)</label>
              <code className="text-[10px] text-gray-500 font-mono break-all">{selectedElementData.id}</code>
            </div>
          </div>
        </div>
      )}

      {/* Input Element Properties - Label, Placeholder */}
      {isInputElement && (
        <div className="bg-gradient-to-br from-white dark:from-gray-800 to-purple-50/30 rounded-xl p-4 border border-purple-200 shadow-sm dark:shadow-gray-900/20">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1.5 bg-purple-100 text-purple-600 rounded-lg">
              <Edit2 className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Input Properties</h4>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Label</label>
              <input
                type="text"
                value={selectedElementData.label || ''}
                onChange={(e) => handleInputPropertyChange('label', e.target.value)}
                placeholder="Enter label text..."
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all"
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Placeholder</label>
              <input
                type="text"
                value={selectedElementData.placeholder || ''}
                onChange={(e) => handleInputPropertyChange('placeholder', e.target.value)}
                placeholder="Enter placeholder text..."
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all"
              />
            </div>
            {selectedElementData.type === 'number-input' && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Min</label>
                  <NumericInput
                    value={selectedElementData.config?.min ?? ''}
                    fallback={null}
                    onChange={(val) => handleInputPropertyChange('config', { ...selectedElementData.config, min: val })}
                    placeholder="Min"
                    className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-purple-400"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Max</label>
                  <NumericInput
                    value={selectedElementData.config?.max ?? ''}
                    fallback={null}
                    onChange={(val) => handleInputPropertyChange('config', { ...selectedElementData.config, max: val })}
                    placeholder="Max"
                    className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-purple-400"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Step</label>
                  <NumericInput
                    value={selectedElementData.config?.step ?? 1}
                    fallback={1}
                    onChange={(val) => handleInputPropertyChange('config', { ...selectedElementData.config, step: val || 1 })}
                    placeholder="Step"
                    className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-purple-400"
                  />
                </div>
              </div>
            )}
            {selectedElementData.type === 'text-input' && (
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedElementData.config?.multiline || false}
                    onChange={(e) => handleInputPropertyChange('config', { ...selectedElementData.config, multiline: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Multi-line (textarea)</span>
                </label>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Max Length</label>
                  <NumericInput
                    value={selectedElementData.config?.maxLength ?? ''}
                    fallback={null}
                    onChange={(val) => handleInputPropertyChange('config', { ...selectedElementData.config, maxLength: val })}
                    placeholder="No limit"
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-purple-400"
                  />
                </div>
              </div>
            )}
            <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors">
              <input
                type="checkbox"
                checked={selectedElementData.config?.required || selectedElementData.required || false}
                onChange={(e) => handleInputPropertyChange('config', { ...selectedElementData.config, required: e.target.checked })}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Required</span>
            </label>
          </div>
        </div>
      )}


      {/* Element Position on Canvas */}
      {(() => {
        const canvasLayout = screens[currentScreenIndex]?.layout || {};
        const canvasW = canvasLayout.width || 810;
        const canvasH = canvasLayout.height || 1080;
        const elW = selectedElementData.size?.width || 100;
        const elH = selectedElementData.size?.height || 100;

        const alignElement = (anchorX, anchorY) => {
          const x = anchorX === 'left' ? 0 : anchorX === 'center' ? (canvasW - elW) / 2 : canvasW - elW;
          const y = anchorY === 'top' ? 0 : anchorY === 'middle' ? (canvasH - elH) / 2 : canvasH - elH;
          const updatedScreens = screens.map((screen, idx) => {
            if (idx !== currentScreenIndex) return screen;
            return {
              ...screen,
              elements: screen.elements.map(el =>
                el.id === selectedElement
                  ? { ...el, position: { x: Math.max(0, Math.round(x)), y: Math.max(0, Math.round(y)) } }
                  : el
              )
            };
          });
          setScreens(updatedScreens);
        };

        const cellClass = "w-6 h-6 flex items-center justify-center rounded border border-gray-200 dark:border-gray-700 hover:bg-primary-100 hover:border-primary-400 cursor-pointer transition-colors group";

        return (
          <div className="bg-gradient-to-br from-white dark:from-gray-800 to-gray-50 dark:to-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1.5 bg-gray-100 dark:bg-gray-800 text-gray-600 rounded-lg">
                <Layout className="w-3.5 h-3.5" />
              </div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Position on Canvas</h4>
            </div>
            <div className="flex items-center gap-3">
              <div className="grid grid-cols-3 gap-0.5 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                {['top','middle','bottom'].map(row =>
                  ['left','center','right'].map(col => (
                    <button
                      key={`el-${row}-${col}`}
                      onClick={() => alignElement(col, row)}
                      className={cellClass}
                      title={`${row} ${col}`}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400 group-hover:bg-primary-600" />
                    </button>
                  ))
                )}
              </div>
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-gray-400 w-4">X</span>
                  <NumericInput
                    value={Math.round(selectedElementData.position?.x || 0)}
                    fallback={0}
                    onChange={(val) => {
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, position: { ...el.position, x: val } } : el) };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="w-full px-2 py-1 text-[10px] border border-gray-200 dark:border-gray-700 rounded text-center"
                  />
                  <span className="text-[9px] text-gray-400 w-4">Y</span>
                  <NumericInput
                    value={Math.round(selectedElementData.position?.y || 0)}
                    fallback={0}
                    onChange={(val) => {
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, position: { ...el.position, y: val } } : el) };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="w-full px-2 py-1 text-[10px] border border-gray-200 dark:border-gray-700 rounded text-center"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-gray-400 w-4">W</span>
                  <NumericInput
                    value={Math.round(selectedElementData.size?.width || 0)}
                    fallback={50}
                    min={50}
                    onChange={(val) => {
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, size: { ...el.size, width: Math.max(50, val) } } : el) };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="w-full px-2 py-1 text-[10px] border border-gray-200 dark:border-gray-700 rounded text-center"
                  />
                  <span className="text-[9px] text-gray-400 w-4">H</span>
                  <NumericInput
                    value={Math.round(selectedElementData.size?.height || 0)}
                    fallback={30}
                    min={30}
                    onChange={(val) => {
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, size: { ...el.size, height: Math.max(30, val) } } : el) };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="w-full px-2 py-1 text-[10px] border border-gray-200 dark:border-gray-700 rounded text-center"
                  />
                </div>
              </div>
            </div>

          </div>
        );
      })()}

      {/* Button Behavior - Action, Target Screen */}
      {selectedElementData.type === 'button' && (
        <div className="bg-gradient-to-br from-white dark:from-gray-800 to-primary-50/30 rounded-xl p-4 border border-primary-200 shadow-sm dark:shadow-gray-900/20">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1.5 bg-primary-100 text-primary-600 rounded-lg">
              <MousePointer className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Button Behavior</h4>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Action</label>
              <select 
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-400 transition-all"
                value={selectedElementData.action || 'next'}
                onChange={(e) => {
                  const newAction = e.target.value;
                  
                  // Map actions to default style presets
                  const actionPresetMap = {
                    next: 'primary',
                    back: 'secondary',
                    jump: 'primary',
                    submit: 'success',
                    skip: 'outline'
                  };
                  
                  // Map actions to default button text
                  const actionTextMap = {
                    next: 'Next',
                    back: 'Back',
                    jump: 'Continue',
                    submit: 'Submit',
                    skip: 'Next'
                  };
                  
                  const presetKey = actionPresetMap[newAction] || 'primary';
                  const preset = BUTTON_STYLE_PRESETS[presetKey];
                  const defaultText = actionTextMap[newAction] || 'Button';
                  
                  const updatedScreens = screens.map((screen, idx) => {
                    if (idx === currentScreenIndex) {
                      return {
                        ...screen,
                        elements: screen.elements.map(el =>
                          el.id === selectedElement
                            ? { 
                                ...el, 
                                action: newAction,
                                content: defaultText,
                                label: defaultText,
                                style: {
                                  ...(el.style || {}),
                                  backgroundColor: preset.backgroundColor,
                                  textColor: preset.textColor,
                                  borderRadius: preset.borderRadius,
                                  borderWidth: preset.borderWidth,
                                  borderColor: preset.borderColor,
                                  preset: presetKey
                                }
                              }
                            : el
                        )
                      };
                    }
                    return screen;
                  });
                  setScreens(updatedScreens);
                }}
              >
                <option value="next">Next Screen (follow defaults)</option>
                <option value="back">Previous Screen</option>
                <option value="jump">Jump to Specific Screen</option>
                <option value="submit">Submit Survey</option>
                <option value="skip">Custom (use skip logic)</option>
              </select>
            </div>

            {selectedElementData.action === 'jump' && (
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Target Screen</label>
                <select
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-400 transition-all"
                  value={selectedElementData.targetScreen || ''}
                  onChange={(e) => {
                    const updatedScreens = screens.map((screen, idx) => {
                      if (idx === currentScreenIndex) {
                        return {
                          ...screen,
                          elements: screen.elements.map(el =>
                            el.id === selectedElement
                              ? { ...el, targetScreen: e.target.value }
                              : el
                          )
                        };
                      }
                      return screen;
                    });
                    setScreens(updatedScreens);
                  }}
                >
                  <option value="">Select a screen...</option>
                  {screens.map((screen, idx) => (
                    <option key={screen.id} value={screen.id}>
                      {screen.name || `Screen ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedElementData.action === 'submit' && (
              <div className="p-2.5 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-green-800">
                    This button will complete the survey immediately.
                  </p>
                </div>
              </div>
            )}

            {selectedElementData.action === 'skip' && (
              <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  Configure skip logic rules in the <strong>Navigation</strong> tab.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Button Appearance - Styling controls */}
      {selectedElementData.type === 'button' && (
        <div className="bg-gradient-to-br from-white dark:from-gray-800 to-purple-50/30 rounded-xl p-4 border border-purple-200 shadow-sm dark:shadow-gray-900/20">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1.5 bg-purple-100 text-purple-600 rounded-lg">
              <Palette className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Button Appearance</h4>
          </div>
          
          <div className="space-y-4">
            {/* Platform/Theme Dropdown */}
            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Button Theme</label>
              <select
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-100 focus:border-purple-400 transition-all"
                value={selectedElementData.style?.theme || 'default'}
                onChange={(e) => applyThemePreset(e.target.value)}
              >
                {Object.entries(BUTTON_THEME_PRESETS).map(([key, theme]) => (
                  <option key={key} value={key}>
                    {theme.name} - {theme.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Style Presets */}
            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-2 uppercase tracking-wide">Quick Styles</label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(BUTTON_STYLE_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyStylePreset(key)}
                    className={`px-2 py-1.5 text-[10px] font-medium rounded-lg border-2 transition-all ${
                      selectedElementData.style?.preset === key
                        ? 'border-purple-500 ring-2 ring-purple-200'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                    style={{
                      backgroundColor: preset.backgroundColor,
                      color: preset.textColor,
                      borderRadius: `${Math.min(parseInt(preset.borderRadius), 12)}px`
                    }}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Controls */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Background</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedElementData.style?.backgroundColor || '#0d9488'}
                    onChange={(e) => updateButtonStyle('backgroundColor', e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border border-gray-200 dark:border-gray-700"
                  />
                  <input
                    type="text"
                    value={selectedElementData.style?.backgroundColor || '#0d9488'}
                    onChange={(e) => updateButtonStyle('backgroundColor', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono"
                    placeholder="#0d9488"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Text Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedElementData.style?.textColor || '#ffffff'}
                    onChange={(e) => updateButtonStyle('textColor', e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border border-gray-200 dark:border-gray-700"
                  />
                  <input
                    type="text"
                    value={selectedElementData.style?.textColor || '#ffffff'}
                    onChange={(e) => updateButtonStyle('textColor', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>

            {/* Border Controls */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Border Radius</label>
                <div className="flex items-center gap-1">
                  <NumericInput
                    min={0}
                    max={9999}
                    value={selectedElementData.style?.borderRadius || '8'}
                    fallback={8}
                    onChange={(val) => updateButtonStyle('borderRadius', String(val))}
                    className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                  />
                  <span className="text-[10px] text-gray-500">px</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Border Width</label>
                <div className="flex items-center gap-1">
                  <NumericInput
                    min={0}
                    max={10}
                    value={selectedElementData.style?.borderWidth || '0'}
                    fallback={0}
                    onChange={(val) => updateButtonStyle('borderWidth', String(val))}
                    className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                  />
                  <span className="text-[10px] text-gray-500">px</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Border Color</label>
                <input
                  type="color"
                  value={selectedElementData.style?.borderColor || '#0d9488'}
                  onChange={(e) => updateButtonStyle('borderColor', e.target.value)}
                  className="w-full h-8 rounded cursor-pointer border border-gray-200 dark:border-gray-700"
                />
              </div>
            </div>

            {/* Live Preview */}
            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Preview</label>
              <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <button
                  className="px-4 py-2 font-medium transition-all"
                  style={{
                    backgroundColor: selectedElementData.style?.backgroundColor || '#0d9488',
                    color: selectedElementData.style?.textColor || '#ffffff',
                    borderRadius: `${selectedElementData.style?.borderRadius || 8}px`,
                    borderWidth: `${selectedElementData.style?.borderWidth || 0}px`,
                    borderStyle: 'solid',
                    borderColor: selectedElementData.style?.borderColor || '#0d9488'
                  }}
                >
                  {selectedElementData.content || selectedElementData.label || 'Button'}
                </button>
              </div>
            </div>

            {/* Advanced CSS - Collapsible */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <button
                onClick={() => setShowAdvancedCss(!showAdvancedCss)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-1.5">
                  <Code className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-[10px] font-medium text-gray-700 uppercase tracking-wide">Advanced CSS</span>
                </div>
                {showAdvancedCss ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
              
              {showAdvancedCss && (
                <div className="mt-2">
                  <textarea
                    value={selectedElementData.style?.customCss || ''}
                    onChange={(e) => updateButtonStyle('customCss', e.target.value)}
                    placeholder="/* Custom CSS */&#10;box-shadow: 0 4px 6px rgba(0,0,0,0.1);&#10;text-transform: uppercase;"
                    className="w-full px-3 py-2 text-xs font-mono bg-gray-900 text-green-400 border border-gray-700 rounded-lg resize-none"
                    rows={4}
                  />
                  <p className="text-[9px] text-gray-500 mt-1.5">
                    Enter custom CSS properties. These will override the controls above.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => toggleLockElement(selectedElement)}
        className={`w-full flex items-center justify-center px-3 py-2 text-sm border rounded transition-colors ${
          isElementLocked(selectedElement)
            ? 'text-amber-700 bg-amber-50 border-amber-300 hover:bg-amber-100'
            : 'text-gray-700 bg-gray-50 dark:bg-gray-900 border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        {isElementLocked(selectedElement) ? (
          <>
            <Lock className="w-4 h-4 mr-2" />
            Unlock
          </>
        ) : (
          <>
            <Unlock className="w-4 h-4 mr-2" />
            Lock
          </>
        )}
      </button>
      <button
        onClick={() => deleteElement(selectedElement)}
        className="w-full flex items-center justify-center px-3 py-2.5 text-sm text-red-600 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl hover:from-red-100 hover:to-red-200 transition-all shadow-sm dark:shadow-gray-900/20 font-medium"
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Delete Element
      </button>
    </div>
  );
};

export default PropertiesPanel;
